// Display helpers for the Templates page: type labels, the facts shown in the
// detail panel, and the chips shown on list rows and the detail header.
//
// Facts and chips are declared as plain lists of objects, each computing its
// own value from { template, endoConfig, requestCount }. Everything here is
// presentation-only — it never changes what is fetched.
import * as lineage from "./templateLineage";
import { brandTypeStyle } from "./templateTypeStyle";
import * as support from "components/admin/templates/Templates.support";

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

const SHORT_TYPE_LABELS = {
  EXCEL: "Excel",
  WORD: "Word (legacy)",
  WORD_JINJA: "Word",
  WORD_JINJA_ENDORSEMENT: "Word endorsement",
  ENDORSEABLE_WORD_JINJA: "Word policy",
  PDF_ENDORSEMENT: "PDF endorsement",
  ENDORSABLE_PDF: "PDF policy",
};

export const shortTypeLabel = (form) =>
  SHORT_TYPE_LABELS[form] ?? form ?? "Unknown";

export const fullTypeLabel = (form) =>
  support.templateTypes.find((t) => t.key === form)?.label ?? form ?? "Unknown";

export const formNumberDisplay = (template) =>
  template?.formNumber || (template?.title ?? "").replace(/_/g, " ").trim();

const friendlyNameFromFilename = (filename) => {
  const noExt = (filename ?? "").replace(/\.[^.]+$/, "").trim();
  const dashIdx = noExt.indexOf(" - ");
  return dashIdx >= 0 ? noExt.slice(dashIdx + 3).trim() : noExt;
};

export const templateLabel = (template) => {
  const name = friendlyNameFromFilename(template?.filename);
  if (!name || name === template?.title) return null;
  return name;
};

export const endorsementInputLabel = (input) =>
  input?.textFieldProps?.label ||
  input?.label ||
  input?.selectProps?.label ||
  "(unlabelled input)";

// Turn an enum / code value into a human-readable label:
//   "NEW_TEMPLATE" -> "New template", "IN_PROGRESS" -> "In progress",
//   "toggle" -> "Toggle", "text" -> "Text".
export const humanize = (value) => {
  if (value == null || value === "") return value;
  const s = String(value).replace(/_/g, " ").toLowerCase().trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const templateStatus = (template) => {
  if (lineage.isDeprecated(template)) return "Deprecated";
  if (template?.active === false) return "Inactive";
  return "Active";
};

// ---------------------------------------------------------------------------
// Value formatting
// ---------------------------------------------------------------------------

const isEmpty = (v) =>
  v == null || v === "" || (Array.isArray(v) && v.length === 0);

// Flatten an array / object to a readable string: arrays by item name/label
// (or the item itself when primitive), objects by their keys.
const asText = (v) => {
  if (Array.isArray(v)) {
    return v
      .map((x) =>
        x == null ? "" : x.name ?? x.label ?? (typeof x === "object" ? "" : x)
      )
      .filter((s) => s !== "")
      .join(", ");
  }
  if (v && typeof v === "object") return Object.keys(v).join(", ");
  return v;
};

const variableCount = (template) =>
  Object.keys(template?.variables ?? {}).length;

const includedEndorsementsSummary = (template) =>
  template?.includedEndorsements
    ? `${template.includedEndorsementsMode ?? "INCLUDE"} · ${
        template.includedEndorsements.length
      }`
    : "All";

// ---------------------------------------------------------------------------
// Facts — { label, value } rows for the detail panel; empty rows are dropped
// ---------------------------------------------------------------------------

export const TEMPLATE_FACTS = [
  { label: "Type", value: ({ template }) => fullTypeLabel(template?.form) },
  { label: "File", value: ({ template }) => template?.filename },
  { label: "Variables", value: ({ template }) => asText(template?.variables) },
  {
    label: "Policy forms",
    value: ({ template }) => asText(template?.policyForms),
  },
  {
    label: "Included endorsements",
    value: ({ template }) => includedEndorsementsSummary(template),
  },
  {
    label: "Email targets",
    value: ({ template }) => asText(template?.allowedEmailTargets),
  },
  { label: "Linked requests", value: ({ requestCount }) => requestCount ?? 0 },
];

export const ENDORSEMENT_FACTS = [
  {
    label: "Endorsement title",
    value: ({ endoConfig }) => endoConfig?.endorsementTitle,
  },
  { label: "Label", value: ({ endoConfig }) => endoConfig?.label },
  { label: "Form number", value: ({ endoConfig }) => endoConfig?.formNumber },
  { label: "Edition", value: ({ endoConfig }) => endoConfig?.editionDate },
  {
    label: "Selection type",
    value: ({ endoConfig }) => humanize(endoConfig?.type),
  },
];

export const buildFacts = (facts, ctx) =>
  facts
    .map(({ label, value }) => ({ label, value: value(ctx) }))
    .filter((fact) => !isEmpty(fact.value));

// ---------------------------------------------------------------------------
// Chips — each builder returns Chip props (or null to omit the chip)
// ---------------------------------------------------------------------------

const STATUS_STYLE = {
  Active: { color: "success", variant: "outlined" },
  Inactive: { color: "warning", variant: "filled" },
  Deprecated: { color: "default", variant: "filled" },
};

const typeChip = ({ template }) => {
  const style = brandTypeStyle(template?.form);
  return {
    label: shortTypeLabel(template?.form),
    variant: "outlined",
    sx: {
      bgcolor: style.bg,
      color: style.fg,
      borderColor: style.border,
      fontWeight: 600,
    },
  };
};

const editionChip = ({ endoConfig }) => {
  const edition = endoConfig?.editionDate;
  return isEmpty(edition)
    ? null
    : { label: String(edition), variant: "outlined" };
};

const statusChip = ({ template }) => {
  const status = templateStatus(template);
  return { label: status, ...STATUS_STYLE[status] };
};

const varsChip = ({ template }) => {
  const n = variableCount(template);
  if (!n) return null;
  return { label: `${n} var${n === 1 ? "" : "s"}`, variant: "outlined" };
};

const requestsChip = ({ requestCount }) => {
  const n = requestCount ?? 0;
  if (!n) return null;
  return {
    label: `${n} request${n === 1 ? "" : "s"}`,
    color: "info",
    variant: "outlined",
  };
};

export const LIST_CHIPS = [
  typeChip,
  editionChip,
  statusChip,
  varsChip,
  requestsChip,
];

export const DETAIL_CHIPS = [typeChip, editionChip, statusChip];

export const buildChips = (chips, ctx) =>
  chips.map((chip) => chip(ctx)).filter(Boolean);

// ---------------------------------------------------------------------------
// List search & type filter
// ---------------------------------------------------------------------------

// Fields the list's search box matches against.
export const SEARCH_FIELDS = [
  (template) => formNumberDisplay(template),
  (template) => templateLabel(template),
  (template) => template?.title,
  (template) => template?.filename,
];

// Groups offered by the type filter.
export const TYPE_GROUPS = {
  Policies: ["ENDORSEABLE_WORD_JINJA", "ENDORSABLE_PDF"],
  Endorsements: ["WORD_JINJA_ENDORSEMENT", "PDF_ENDORSEMENT"],
  "Other documents": ["WORD_JINJA", "WORD", "EXCEL"],
};
